import { useApiListingParams } from '$hooks/actions'
import { useAppRoleService } from '$hooks/services'
import { APIWithPagerMeta, TID } from '$services/api'
import { defaultBackground, borderNeutral20, flexRow } from '$styles/common.css'
import { useEffect, useMemo, useState } from 'react'
import { useAssignRoleStore } from '$hooks/stores'
import { themeVars } from '@genie-fintech/ui/style/theme'
import { cn } from '$app/utils'
import Pagination from '$components/Pagination'
import { markedDefaultKey } from '@genie-fintech/ui/style/theme/colors/functions'
import PreviewPermissionDrawer from '$blocks/PreviewPermissionDrawer'
import { AssignRolesSidebar } from './Sidebar'
import {
  ASSIGN_ROLES_SIDE_MENUS,
  ASSIGN_ROLES_VALUE
} from '$layouts/LayoutWithSidebar/constants'
import { AssignTable } from './AssignTable'
import SearchBox from '$components/SearchBox'
import GroupFilter from '$blocks/GroupFilter'
import { useGroupFilter } from '$blocks/GroupFilter/useGroupFilter'
import { useUnmount } from 'ahooks'
const { colors } = themeVars

interface IProps {
  onChange: (id: TID[]) => void
  roles: TID[]
  errorMessage?: string
  appId?: TID
}

const AssignRoles = ({ onChange, roles, errorMessage, appId }: IProps) => {
  const [selectedMenu, setSelectedMenu] = useState<string>('all-roles')
  const assignedRoles = useAssignRoleStore(s => s.roles)
  const defaultSelectedRoles = useAssignRoleStore(s => s.selectedRoles)
  const resetStore = useAssignRoleStore(s => s.reset)

  const [searchLocalValue, setSearchLocalValue] = useState<string>('')

  const { filteredGroupIds, onChangeFilteredGroupIds } = useGroupFilter(() =>
    jump(1)
  )

  const {
    fetchAppRoleList,
    fetchingAppRoleList,
    roles: { list, meta }
  } = useAppRoleService()

  const [selectedRole, setSelectedRole] = useState<{
    id: number
    name: string
  }>()

  const {
    searchValue,
    updateSearchValue,
    debouncedSearchValue,
    pagerProps,
    perPage
  } = useApiListingParams({ total: meta?.total, perPage: 10 })

  const { currentPage, jump } = pagerProps

  const filteredRoles = useMemo(() => {
    return assignedRoles.filter(role =>
      role.name.toLowerCase().includes(searchLocalValue.toLowerCase())
    )
  }, [assignedRoles, searchLocalValue])

  const { pagerProps: localPagerProps, perPage: localPerPage } =
    useApiListingParams({
      total: filteredRoles.length,
      perPage: 10
    })

  const { currentPage: localCurrentPage } = localPagerProps

  const localRoles = useMemo(
    () =>
      filteredRoles.slice(
        localPerPage * (localCurrentPage - 1),
        localPerPage * localCurrentPage
      ),
    [filteredRoles, localPerPage, localCurrentPage]
  )

  const localMeta: APIWithPagerMeta['meta'] = {
    total: filteredRoles.length,
    from:
      filteredRoles.length === 0
        ? 0
        : localPerPage * (localCurrentPage - 1) + 1,
    to: Math.min(localPerPage * localCurrentPage, filteredRoles.length),
    current_page: localCurrentPage,
    last_page: Math.ceil(filteredRoles.length / localPerPage),
    per_page: localPerPage
  }

  useEffect(() => {
    if (!appId) return
    fetchAppRoleList({
      page: currentPage,
      per_page: perPage,
      application_id: appId,
      group_ids: filteredGroupIds,
      q: debouncedSearchValue
    })
  }, [
    appId,
    currentPage,
    perPage,
    fetchAppRoleList,
    debouncedSearchValue,
    filteredGroupIds
  ])

  useUnmount(() => {
    resetStore()
  })

  const isSelectedRoles = useMemo(
    () => selectedMenu === ASSIGN_ROLES_VALUE.SELECTED,
    [selectedMenu]
  )

  const roleData = useMemo(() => {
    if (!isSelectedRoles) {
      return list.map(v => ({
        ...v,
        isDisabled: !!defaultSelectedRoles.find(d => d.id === v.id)
      }))
    } else {
      return localRoles
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMenu, list, localRoles])

  const totalCount = useMemo(() => {
    if (!isSelectedRoles) {
      return meta?.total ?? 0
    }
    return filteredRoles.length
  }, [isSelectedRoles, filteredRoles.length, meta?.total])

  return (
    <>
      <div
        className={cn(
          'flex flex-row rounded-lg gap-6 px-4 py-4',
          defaultBackground,
          borderNeutral20
        )}
      >
        <div className="w-1/5">
          <AssignRolesSidebar
            menus={ASSIGN_ROLES_SIDE_MENUS}
            selectedMenu={selectedMenu}
            onMenuClick={setSelectedMenu}
          />
        </div>
        <article
          className={cn('flex flex-col rounded-lg gap-6 px-4 py-4 w-4/5')}
        >
          <article className="flex flex-col gap-1">
            <p className="font-semibold">ASSIGNED ROLES</p>
            <p className="text-xs text-[--colors-neutral-50]">
              Define and assign roles to control access and permissions within
              the system.
            </p>
            {errorMessage && (
              <p
                className="text-xs font-medium"
                style={{ color: colors.danger[markedDefaultKey] }}
              >
                {errorMessage}
              </p>
            )}
          </article>

          <article className="flex flex-col gap-2">
            <article className="flex gap-2 items-center">
              <article className={flexRow} style={{ flex: 1 }}>
                <SearchBox
                  value={isSelectedRoles ? searchLocalValue : searchValue}
                  onChange={value => {
                    if (isSelectedRoles) {
                      setSearchLocalValue(value)
                      return
                    }
                    updateSearchValue(value)
                  }}
                  placeholder="Search here..."
                />
              </article>

              {!isSelectedRoles && (
                <GroupFilter
                  appId={appId}
                  filteredGroupIds={filteredGroupIds}
                  onChangeFilteredGroupIds={onChangeFilteredGroupIds}
                />
              )}

              <span style={{ flex: 1 }} />

              <p className="text-[--colors-text-disabled] text-sm font-medium">
                {totalCount} ROLE{totalCount > 1 && 'S'}
              </p>
            </article>

            <AssignTable
              data={roleData}
              onChange={onChange}
              roles={roles}
              setSelectedRole={setSelectedRole}
              isLoading={fetchingAppRoleList}
              currentPage={!isSelectedRoles ? currentPage : localCurrentPage}
              perPage={!isSelectedRoles ? perPage : localPerPage}
            />

            {selectedMenu === ASSIGN_ROLES_VALUE.ALL ? (
              !!roleData.length && (
                <Pagination meta={meta} pagerProps={pagerProps} />
              )
            ) : (
              <Pagination pagerProps={localPagerProps} meta={localMeta} />
            )}
          </article>
        </article>
      </div>

      <PreviewPermissionDrawer
        role={selectedRole}
        appId={appId}
        onClose={() => setSelectedRole(undefined)}
      />
    </>
  )
}

export default AssignRoles
